import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Upozornění na novou verzi — `/admin/api/v1/version-notice` (admin API, JWT cookie,
 * jakýkoli přihlášený uživatel).
 *
 * Verzi určuje **server** z build informací; klient ji neposílá, aby nešlo `ack`
 * podvrhnout a upozornění potlačit dřív, než ho uživatel uvidí.
 *
 * `show` je `false` u účtu, který ještě žádnou verzi neviděl — čerstvý účet nemá s čím
 * srovnávat a první dialog by oznamoval sám sebe. Hodnota se mu jen tiše zapíše (viz
 * `useAcknowledgeVersion` volané při zavření dialogu i bez zobrazení).
 */
export interface VersionNotice {
  show: boolean;
  version: string;
  previousVersion: string | null;
  /** Sekce changelogu pro `version`; `null`, když ji server nenašel. */
  changelogHtml: string | null;
  /** Slug stránky nápovědy s celým changelogem. */
  docsSlug: string;
}

const KEY = ["version-notice"];

export function useVersionNotice() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiFetch<VersionNotice>("/admin/api/v1/version-notice"),
    // Verze se během sezení nemění; opakovaný dotaz při každém přepnutí okna je zbytečný.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useAcknowledgeVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>("/admin/api/v1/version-notice/ack", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
